import { useCallback, useMemo, useRef } from 'react'

/** Above this, render a plain textarea to avoid huge DOM from line gutters. */
export const LINED_LINE_NUMBERS_MAX_LINES = 20_000

function lineCountFor(value: string): number {
  if (value.length === 0) return 1
  return value.split('\n').length
}

/** Monospace gutter width in `ch`, wide enough for right-aligned line numbers without crowding text. */
export function linedGutterWidthCh(lineCount: number): number {
  const digits = String(Math.max(1, lineCount)).length
  return Math.min(12, Math.max(3.5, digits + 1.5))
}

export type LinedTextAreaDensity = 'default' | 'compact'

/** Fixed viewport height so long files scroll inside the pane (gutter must not grow the layout). */
export const LINED_TEXT_AREA_OUTER_CLASS: Record<LinedTextAreaDensity, string> = {
  default: 'h-[280px] max-h-[calc(100vh-10rem)] lg:h-[360px]',
  compact: 'h-[160px] max-h-[calc(100vh-12rem)] lg:h-[200px]',
}

/**
 * Mobile / stacked layout: cap output height so it scrolls internally. On lg, ToolWorkspace fixes the grid
 * height. Use max-h-full so the pane fills that region (avoid intrinsic content expanding the row).
 */
export const TOOL_OUTPUT_VIEWPORT_CAP_CLASS =
  'max-h-[min(34rem,calc(100vh-16rem))] lg:max-h-full'

/**
 * Stacked (non-lg) tool layout: grid rows use auto height, so `flex-1` editors collapse without a floor.
 * Caps height on largest phones so the page still scrolls to output + stats.
 */
export const TOOL_WORKSPACE_MOBILE_EDITOR_MIN = 'min-h-[min(42vh,18rem)] lg:min-h-0'

/** lg: fixed main card height so the shell does not jump between empty vs large output. */
export const TOOL_WORKSPACE_GRID_LG_CLASS =
  'lg:grid-rows-[minmax(0,1fr)] lg:h-[min(34rem,calc(100vh-16rem))] lg:min-h-[min(34rem,calc(100vh-16rem))] lg:max-h-[min(34rem,calc(100vh-16rem))]'

/**
 * Fill the workspace I/O column (inputs + output). Pair with a flex column parent and `lg:h-full` on the
 * pane root. Do not add overflow-* here; consumers set overflow-auto or scroll.
 */
export const LINED_TEXT_AREA_FILL_CLASS = `flex-1 basis-0 w-full min-w-0 ${TOOL_WORKSPACE_MOBILE_EDITOR_MIN} ${TOOL_OUTPUT_VIEWPORT_CAP_CLASS}`

export type LinedTextAreaProps = {
  value: string
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement>
  readOnly?: boolean
  maxLength?: number
  rows?: number
  placeholder?: string
  'aria-label'?: string
  density?: LinedTextAreaDensity
  /**
   * Soft-wrap long lines inside the pane. When on, line gutters are hidden (native textarea cannot align
   * wrapped rows to logical line numbers).
   */
  wrapLines?: boolean
  /** Expand vertically inside a ToolWorkspace I/O column (input or output pane). */
  fillHeight?: boolean
}

export default function LinedTextArea({
  value,
  onChange,
  readOnly,
  maxLength,
  rows,
  placeholder,
  'aria-label': ariaLabel,
  density = 'default',
  wrapLines = false,
  fillHeight = false,
}: LinedTextAreaProps) {
  const boxHeight = fillHeight ? LINED_TEXT_AREA_FILL_CLASS : LINED_TEXT_AREA_OUTER_CLASS[density]
  const taRef = useRef<HTMLTextAreaElement>(null)
  const gutterRef = useRef<HTMLDivElement>(null)

  const lineCount = useMemo(() => lineCountFor(value), [value])
  const showGutter = lineCount <= LINED_LINE_NUMBERS_MAX_LINES
  const gutterCh = useMemo(() => linedGutterWidthCh(lineCount), [lineCount])

  const onScroll = useCallback(() => {
    const ta = taRef.current
    const g = gutterRef.current
    if (ta && g) g.scrollTop = ta.scrollTop
  }, [])

  if (!showGutter || wrapLines) {
    const wrapCls = wrapLines
      ? 'overflow-y-auto overflow-x-hidden whitespace-pre-wrap break-words'
      : 'overflow-auto whitespace-pre'
    return (
      <textarea
        value={value}
        onChange={onChange}
        readOnly={readOnly}
        maxLength={maxLength}
        rows={rows}
        wrap={wrapLines ? 'soft' : 'off'}
        spellCheck={false}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className={`form-well ${boxHeight} min-w-0 w-full max-w-full resize-none p-4 font-mono text-sm leading-6 text-on-surface grid-bg ${wrapCls}`}
      />
    )
  }

  return (
    <div
      className={`form-well grid-bg flex min-h-0 w-full min-w-0 ${boxHeight} overflow-hidden rounded-md focus-within:ring-2 focus-within:ring-outline/40`}
    >
      <div
        ref={gutterRef}
        className="box-border h-full min-h-0 shrink-0 overflow-y-auto overflow-x-hidden border-r border-outline-variant/20 bg-surface-container-lowest/90 py-4 pl-2 pr-1.5 font-mono text-sm leading-6 text-on-surface-variant tabular-nums pointer-events-none"
        style={{ width: `${gutterCh}ch`, minWidth: '3rem' }}
        aria-hidden
      >
        {Array.from({ length: lineCount }, (_, i) => (
          <div key={i} className="h-6 text-right">
            {i + 1}
          </div>
        ))}
      </div>
      <textarea
        ref={taRef}
        value={value}
        onChange={onChange}
        readOnly={readOnly}
        maxLength={maxLength}
        rows={rows}
        wrap="off"
        spellCheck={false}
        placeholder={placeholder}
        aria-label={ariaLabel}
        onScroll={onScroll}
        className="h-full min-h-0 min-w-0 flex-1 resize-none overflow-auto whitespace-pre border-0 bg-transparent py-4 pr-3 pl-3 font-mono text-sm leading-6 text-on-surface outline-none focus:ring-0"
      />
    </div>
  )
}
